import React, { useState } from 'react';
import { Subcontractor, SubcontractType, GlobalUser, ProjectInfo, Contract, StatementItem } from '../../types';
import { CHILE_REGIONS, cleanRut, formatRut } from '../../utils/chileData';

interface ContractorManagerProps {
    subcontractors: Subcontractor[];
    setSubcontractors: React.Dispatch<React.SetStateAction<Subcontractor[]>>;
    subcontractTypes: SubcontractType[];
    globalUsers: GlobalUser[];
    projects: (ProjectInfo & { id: string })[];
    contracts?: Contract[];
    setContracts?: React.Dispatch<React.SetStateAction<Contract[]>>;
}

const ContractorManager: React.FC<ContractorManagerProps> = ({
    subcontractors,
    setSubcontractors,
    subcontractTypes,
    globalUsers,
    projects,
    contracts = [],
    setContracts
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<'Todos' | 'Activo' | 'Inactivo'>('Todos');
    const [typeFilter, setTypeFilter] = useState<string>('Todos');

    // Modals
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingSubcontractorId, setEditingSubcontractorId] = useState<string | null>(null);

    // Subcontracts (Contracts) modal per contractor
    const [selectedSubForContracts, setSelectedSubForContracts] = useState<Subcontractor | null>(null);
    const [isCreateContractModalOpen, setIsCreateContractModalOpen] = useState(false);

    // Form state for Contractor
    const [contractorForm, setContractorForm] = useState<Omit<Subcontractor, 'id'>>({
        rut: '',
        razonSocial: '',
        giro: '',
        representante: '',
        telefono: '',
        correo: '',
        subcontractTypeId: '',
        fono: '',
        ciudad: '',
        comuna: '',
        status: 'Activo',
        linkedUserIds: []
    });

    // Form state for New Subcontract (Contract)
    const [newContractForm, setNewContractForm] = useState({
        projectId: projects[0]?.id || '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        retention: 5,
        totalAmount: 0,
        notes: '',
        status: 'Activo' as 'Activo' | 'Borrador' | 'En Proceso'
    });

    // Handlers for Contractor
    const handleOpenCreateContractor = () => {
        setEditingSubcontractorId(null);
        setContractorForm({
            rut: '',
            razonSocial: '',
            giro: '',
            representante: '',
            telefono: '',
            correo: '',
            subcontractTypeId: subcontractTypes[0]?.id || '',
            fono: '',
            ciudad: '',
            comuna: '',
            status: 'Activo',
            linkedUserIds: []
        });
        setIsCreateModalOpen(true);
    };

    const handleOpenEditContractor = (sub: Subcontractor) => {
        setEditingSubcontractorId(sub.id);
        setContractorForm({
            rut: sub.rut,
            razonSocial: sub.razonSocial,
            giro: sub.giro || '',
            representante: sub.representante || '',
            telefono: sub.telefono || '',
            correo: sub.correo || '',
            subcontractTypeId: sub.subcontractTypeId || '',
            fono: sub.fono || '',
            ciudad: sub.ciudad || '',
            comuna: sub.comuna || '',
            status: sub.status || 'Activo',
            linkedUserIds: sub.linkedUserIds || []
        });
        setIsCreateModalOpen(true);
    };

    const handleSaveContractor = () => {
        if (!contractorForm.rut.trim() || !contractorForm.razonSocial.trim()) {
            alert("Por favor ingrese al menos el RUT y la Razón Social de la empresa.");
            return;
        }

        if (editingSubcontractorId) {
            setSubcontractors(prev => prev.map(sub => 
                sub.id === editingSubcontractorId ? { ...sub, ...contractorForm } : sub
            ));
        } else {
            const newEntry: Subcontractor = {
                id: `sub-${Date.now()}`,
                ...contractorForm
            };
            setSubcontractors(prev => [...prev, newEntry]);
        }
        setIsCreateModalOpen(false);
        setEditingSubcontractorId(null);
    };

    const handleDeleteContractor = (subId: string, name: string) => {
        const hasAssociatedContracts = contracts.some(c => c.subcontractorId === subId && !c.isDeleted);
        if (hasAssociatedContracts) {
            alert(`La empresa "${name}" tiene subcontratos asociados. Inactívela en su lugar o elimine primero sus subcontratos.`);
            return;
        }

        if (confirm(`¿Está seguro de eliminar la empresa contratista "${name}"?`)) {
            setSubcontractors(prev => prev.filter(s => s.id !== subId));
        }
    };

    const handleToggleContractorStatus = (subId: string) => {
        setSubcontractors(prev => prev.map(s => {
            if (s.id === subId) {
                const newStatus = (s.status || 'Activo') === 'Activo' ? 'Inactivo' : 'Activo';
                return { ...s, status: newStatus };
            }
            return s;
        }));
    };

    // Subcontract handlers
    const handleOpenSubcontractsList = (sub: Subcontractor) => {
        setSelectedSubForContracts(sub);
        setIsCreateContractModalOpen(false);
    };

    const handleOpenCreateSubcontract = () => {
        setNewContractForm({
            projectId: projects[0]?.id || '',
            startDate: new Date().toISOString().split('T')[0],
            endDate: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            retention: 5,
            totalAmount: 0,
            notes: '',
            status: 'Activo'
        });
        setIsCreateContractModalOpen(true);
    };

    const handleSaveSubcontract = () => {
        if (!selectedSubForContracts) return;
        if (!setContracts) {
            alert("Gestor de contratos no configurado para persistencia.");
            return;
        }

        if (newContractForm.totalAmount <= 0) {
            if (!confirm("El monto total del subcontrato es $0. ¿Desea crearlo de todos modos?")) {
                return;
            }
        }

        const newContract: Contract = {
            id: `ctr-${Math.floor(Math.random() * 10000)}`,
            subcontractorId: selectedSubForContracts.id,
            startDate: newContractForm.startDate,
            endDate: newContractForm.endDate,
            retention: Number(newContractForm.retention) || 0,
            totalAmount: Number(newContractForm.totalAmount) || 0,
            status: newContractForm.status,
            notes: newContractForm.notes,
            items: []
        };

        setContracts(prev => [...prev, newContract]);
        setIsCreateContractModalOpen(false);
        alert(`¡Subcontrato creado exitosamente para ${selectedSubForContracts.razonSocial}!`);
    };

    const handleDeleteSubcontract = (contractId: string) => {
        if (!setContracts) return;
        if (confirm("¿Está seguro de anular/eliminar este subcontrato?")) {
            setContracts(prev => prev.map(c => c.id === contractId ? { ...c, isDeleted: true, status: 'Anulado' } : c));
        }
    };

    // Filter list
    const filteredContractors = subcontractors.filter(sub => {
        const matchesQuery = (sub.razonSocial || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (sub.rut || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (sub.giro || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (sub.representante || '').toLowerCase().includes(searchQuery.toLowerCase());

        const subStatus = sub.status || 'Activo';
        const matchesStatus = statusFilter === 'Todos' || subStatus === statusFilter;
        const matchesType = typeFilter === 'Todos' || sub.subcontractTypeId === typeFilter;

        return matchesQuery && matchesStatus && matchesType;
    });

    return (
        <div className="p-4 md:p-8 space-y-6 max-w-7xl mx-auto">
            {/* Header Card */}
            <div className="bg-white dark:bg-[#1a2634] p-6 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-xl font-black text-slate-800 dark:text-white flex items-center gap-2">
                        <span className="material-symbols-outlined text-primary text-[28px]">business_center</span>
                        Maestro de Empresas Contratistas
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        Directorio centralizado de contratistas y creación de subcontratos contractuales.
                    </p>
                </div>
                
                <div className="flex items-center gap-3">
                    <button 
                        onClick={handleOpenCreateContractor}
                        className="bg-primary hover:bg-blue-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                    >
                        <span className="material-symbols-outlined text-[20px]">add_business</span>
                        Nueva Empresa Contratista
                    </button>
                </div>
            </div>

            {/* Filter Toolbar */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-white dark:bg-[#1a2634] p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
                <div className="relative">
                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
                    <input
                        type="text"
                        placeholder="Buscar por Razón Social, RUT, Giro..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-800 dark:text-white placeholder-slate-400 outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                    />
                </div>

                <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500 uppercase whitespace-nowrap">Estado:</span>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value as any)}
                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-300 outline-none focus:ring-2 focus:ring-primary/20"
                    >
                        <option value="Todos">Todos los estados</option>
                        <option value="Activo">Activos</option>
                        <option value="Inactivo">Inactivos</option>
                    </select>
                </div>

                <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500 uppercase whitespace-nowrap">Especialidad:</span>
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-300 outline-none focus:ring-2 focus:ring-primary/20"
                    >
                        <option value="Todos">Todas las modalidades</option>
                        {subcontractTypes.map(t => (
                            <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Contractors Table */}
            <div className="bg-white dark:bg-[#1a2634] rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 dark:bg-[#1e2c3c] border-b border-slate-200 dark:border-slate-800 text-slate-500 text-[11px] font-black uppercase tracking-wider">
                                <th className="p-4">R.U.T / Identificación</th>
                                <th className="p-4">Empresa / Razón Social</th>
                                <th className="p-4">Giro / Especialidad</th>
                                <th className="p-4">Representante & Contacto</th>
                                <th className="p-4 text-center">Subcontratos</th>
                                <th className="p-4 text-center">Estado</th>
                                <th className="p-4 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                            {filteredContractors.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="p-12 text-center text-slate-400 text-sm">
                                        <div className="flex flex-col items-center justify-center gap-2">
                                            <span className="material-symbols-outlined text-[36px] text-slate-300 dark:text-slate-600">domain_disabled</span>
                                            <p className="font-semibold text-slate-500">No se encontraron empresas contratistas registradas.</p>
                                            <p className="text-xs text-slate-400">Haga clic en "Nueva Empresa Contratista" para añadir la primera.</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredContractors.map(sub => {
                                    const subTypeName = subcontractTypes.find(t => t.id === sub.subcontractTypeId)?.name;
                                    const subContractsCount = contracts.filter(c => c.subcontractorId === sub.id && !c.isDeleted).length;
                                    const isActivo = (sub.status || 'Activo') === 'Activo';

                                    return (
                                        <tr key={sub.id} className="hover:bg-slate-50/70 dark:hover:bg-[#1e2a3b]/50 transition-colors">
                                            <td className="p-4 font-mono font-bold text-slate-800 dark:text-slate-200 whitespace-nowrap">
                                                {sub.rut}
                                            </td>
                                            <td className="p-4">
                                                <div className="font-bold text-slate-900 dark:text-white">{sub.razonSocial}</div>
                                                {sub.ciudad && (
                                                    <div className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5">
                                                        <span className="material-symbols-outlined text-[12px]">location_on</span>
                                                        {sub.comuna ? `${sub.comuna}, ${sub.ciudad}` : sub.ciudad}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-4">
                                                <div className="text-slate-700 dark:text-slate-300 font-medium">{sub.giro || 'Sin Giro'}</div>
                                                {subTypeName && (
                                                    <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-100 dark:border-blue-800/50">
                                                        {subTypeName}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-4 text-slate-600 dark:text-slate-400">
                                                {sub.representante && <div className="font-semibold text-slate-800 dark:text-slate-200">{sub.representante}</div>}
                                                {(sub.telefono || sub.fono) && (
                                                    <div className="text-[10px] text-slate-500 font-mono flex items-center gap-1 mt-0.5">
                                                        <span className="material-symbols-outlined text-[12px]">call</span>
                                                        {sub.telefono || sub.fono}
                                                    </div>
                                                )}
                                                {sub.correo && (
                                                    <div className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5">
                                                        <span className="material-symbols-outlined text-[12px]">mail</span>
                                                        {sub.correo}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-4 text-center">
                                                <button
                                                    onClick={() => handleOpenSubcontractsList(sub)}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/60 font-bold hover:bg-indigo-100 transition-colors"
                                                    title="Ver y crear subcontratos para esta empresa"
                                                >
                                                    <span className="material-symbols-outlined text-[16px]">contract</span>
                                                    <span>{subContractsCount} Subcontrato{subContractsCount !== 1 ? 's' : ''}</span>
                                                </button>
                                            </td>
                                            <td className="p-4 text-center">
                                                <button
                                                    onClick={() => handleToggleContractorStatus(sub.id)}
                                                    className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider transition-all ${
                                                        isActivo
                                                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 hover:bg-emerald-200'
                                                            : 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300 hover:bg-rose-200'
                                                    }`}
                                                    title="Haga clic para alternar estado"
                                                >
                                                    {isActivo ? 'Activo' : 'Inactivo'}
                                                </button>
                                            </td>
                                            <td className="p-4 text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button
                                                        onClick={() => handleOpenSubcontractsList(sub)}
                                                        className="p-1.5 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 rounded-lg transition-colors"
                                                        title="Crear Subcontrato"
                                                    >
                                                        <span className="material-symbols-outlined text-[18px]">add_circle</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenEditContractor(sub)}
                                                        className="p-1.5 text-slate-500 hover:text-primary hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                                                        title="Editar Empresa"
                                                    >
                                                        <span className="material-symbols-outlined text-[18px]">edit</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteContractor(sub.id, sub.razonSocial)}
                                                        className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-lg transition-colors"
                                                        title="Eliminar Empresa"
                                                    >
                                                        <span className="material-symbols-outlined text-[18px]">delete</span>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal: Crear / Editar Empresa Contratista */}
            {isCreateModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-150">
                    <div className="bg-white dark:bg-[#1a2634] rounded-3xl shadow-2xl w-full max-w-2xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[90vh] overflow-hidden">
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-900/30">
                            <div>
                                <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                                    <span className="material-symbols-outlined text-primary text-[24px]">
                                        {editingSubcontractorId ? 'edit_square' : 'add_business'}
                                    </span>
                                    {editingSubcontractorId ? 'Editar Empresa Contratista' : 'Nueva Empresa Contratista'}
                                </h3>
                                <p className="text-xs text-slate-500 mt-0.5">Complete la información tributaria y de contacto.</p>
                            </div>
                            <button 
                                onClick={() => setIsCreateModalOpen(false)}
                                className="size-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 transition-colors"
                            >
                                <span className="material-symbols-outlined text-[20px]">close</span>
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto space-y-4">
                            {/* Contractor Form */}
                            {(() => {
                                const cleanCurrentRut = cleanRut(contractorForm.rut);
                                const isRutEntered = cleanCurrentRut.length >= 2;
                                const isRutFound = !editingSubcontractorId && isRutEntered && subcontractors.some(s => cleanRut(s.rut) === cleanCurrentRut);

                                const handleRutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                                    const formatted = formatRut(e.target.value);
                                    const cleaned = cleanRut(formatted);

                                    // Check if existing contractor matches this RUT when creating
                                    if (!editingSubcontractorId && cleaned.length >= 8) {
                                        const match = subcontractors.find(s => cleanRut(s.rut) === cleaned);
                                        if (match) {
                                            setContractorForm({
                                                rut: formatted,
                                                razonSocial: match.razonSocial,
                                                giro: match.giro || '',
                                                representante: match.representante || '',
                                                telefono: match.telefono || '',
                                                correo: match.correo || '',
                                                subcontractTypeId: match.subcontractTypeId || '',
                                                fono: match.fono || '',
                                                ciudad: match.ciudad || '',
                                                comuna: match.comuna || '',
                                                status: match.status || 'Activo',
                                                linkedUserIds: match.linkedUserIds || []
                                            });
                                            return;
                                        }
                                    }

                                    setContractorForm(prev => ({ ...prev, rut: formatted }));
                                };

                                return (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="col-span-1 md:col-span-2">
                                                <label className="block text-[11px] font-black text-slate-600 dark:text-slate-300 uppercase mb-1.5 flex items-center justify-between">
                                                    <span>R.U.T <span className="text-rose-500">*</span></span>
                                                    {!isRutEntered && (
                                                        <span className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold lowercase">
                                                            (Ingrese el RUT para habilitar los demás campos)
                                                        </span>
                                                    )}
                                                    {isRutFound && (
                                                        <span className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 px-2 py-0.5 rounded font-bold">
                                                            ✓ Empresa encontrada en el Maestro (Datos autocompletados)
                                                        </span>
                                                    )}
                                                </label>
                                                <input 
                                                    type="text" 
                                                    placeholder="Ej: 76.543.210-K" 
                                                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl px-3.5 py-2.5 text-xs font-bold text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary font-mono"
                                                    value={contractorForm.rut}
                                                    onChange={handleRutChange}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-[11px] font-black text-slate-600 dark:text-slate-300 uppercase mb-1.5">
                                                    Razón Social <span className="text-rose-500">*</span>
                                                </label>
                                                <input 
                                                    type="text" 
                                                    placeholder="Ej: Instalaciones Eléctricas SpA" 
                                                    disabled={!isRutEntered}
                                                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                                    value={contractorForm.razonSocial}
                                                    onChange={(e) => setContractorForm({ ...contractorForm, razonSocial: e.target.value })}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-[11px] font-black text-slate-600 dark:text-slate-300 uppercase mb-1.5">
                                                    Giro / Rubro
                                                </label>
                                                <input 
                                                    type="text" 
                                                    placeholder="Ej: Obras eléctricas y climatización" 
                                                    disabled={!isRutEntered}
                                                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                                    value={contractorForm.giro}
                                                    onChange={(e) => setContractorForm({ ...contractorForm, giro: e.target.value })}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-[11px] font-black text-slate-600 dark:text-slate-300 uppercase mb-1.5">
                                                    Modalidad de Contratación
                                                </label>
                                                <select
                                                    disabled={!isRutEntered}
                                                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                                    value={contractorForm.subcontractTypeId}
                                                    onChange={(e) => setContractorForm({ ...contractorForm, subcontractTypeId: e.target.value })}
                                                >
                                                    <option value="">Seleccionar Modalidad...</option>
                                                    {subcontractTypes.map(t => (
                                                        <option key={t.id} value={t.id}>{t.name}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-[11px] font-black text-slate-600 dark:text-slate-300 uppercase mb-1.5">
                                                    Representante Legal
                                                </label>
                                                <input 
                                                    type="text" 
                                                    placeholder="Nombre del representante" 
                                                    disabled={!isRutEntered}
                                                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                                    value={contractorForm.representante}
                                                    onChange={(e) => setContractorForm({ ...contractorForm, representante: e.target.value })}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-[11px] font-black text-slate-600 dark:text-slate-300 uppercase mb-1.5">
                                                    Teléfono / Contacto
                                                </label>
                                                <input 
                                                    type="text" 
                                                    placeholder="+56 9 1234 5678" 
                                                    disabled={!isRutEntered}
                                                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                                    value={contractorForm.telefono || contractorForm.fono}
                                                    onChange={(e) => setContractorForm({ ...contractorForm, telefono: e.target.value, fono: e.target.value })}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-[11px] font-black text-slate-600 dark:text-slate-300 uppercase mb-1.5">
                                                    Correo Electrónico
                                                </label>
                                                <input 
                                                    type="email" 
                                                    placeholder="contacto@empresa.cl" 
                                                    disabled={!isRutEntered}
                                                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                                    value={contractorForm.correo}
                                                    onChange={(e) => setContractorForm({ ...contractorForm, correo: e.target.value })}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-[11px] font-black text-slate-600 dark:text-slate-300 uppercase mb-1.5">
                                                    Estado Operativo
                                                </label>
                                                <select
                                                    disabled={!isRutEntered}
                                                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                                    value={contractorForm.status || 'Activo'}
                                                    onChange={(e) => setContractorForm({ ...contractorForm, status: e.target.value })}
                                                >
                                                    <option value="Activo">Activo</option>
                                                    <option value="Inactivo">Inactivo</option>
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-[11px] font-black text-slate-600 dark:text-slate-300 uppercase mb-1.5">
                                                    Región
                                                </label>
                                                <select
                                                    disabled={!isRutEntered}
                                                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                                    value={contractorForm.ciudad}
                                                    onChange={(e) => setContractorForm({ ...contractorForm, ciudad: e.target.value, comuna: '' })}
                                                >
                                                    <option value="">Seleccionar Región...</option>
                                                    {CHILE_REGIONS.map(d => (
                                                        <option key={d.region} value={d.region}>{d.region}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div>
                                                <label className="block text-[11px] font-black text-slate-600 dark:text-slate-300 uppercase mb-1.5">
                                                    Comuna
                                                </label>
                                                <select
                                                    className="w-full bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50"
                                                    value={contractorForm.comuna}
                                                    onChange={(e) => setContractorForm({ ...contractorForm, comuna: e.target.value })}
                                                    disabled={!isRutEntered || !contractorForm.ciudad}
                                                >
                                                    <option value="">Seleccionar Comuna...</option>
                                                    {CHILE_REGIONS.find(d => d.region === contractorForm.ciudad)?.comunas.map(c => (
                                                        <option key={c} value={c}>{c}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        {/* Linked Users */}
                                        <div className="pt-2">
                                            <label className="block text-[11px] font-black text-slate-600 dark:text-slate-300 uppercase mb-1.5">
                                                Usuarios de la Plataforma Vinculados a esta Empresa
                                            </label>
                                            <div className={`border border-slate-200 dark:border-slate-700 rounded-2xl p-3 max-h-36 overflow-y-auto bg-slate-50/50 dark:bg-slate-900/50 space-y-1 ${!isRutEntered ? 'opacity-50 pointer-events-none' : ''}`}>
                                                {globalUsers.map(user => {
                                                    const isChecked = (contractorForm.linkedUserIds || []).includes(user.id);
                                                    return (
                                                        <label key={user.id} className="flex items-center gap-2 p-1.5 hover:bg-white dark:hover:bg-slate-800 rounded-xl cursor-pointer transition-colors">
                                                            <input 
                                                                type="checkbox" 
                                                                checked={isChecked}
                                                                disabled={!isRutEntered}
                                                                onChange={(e) => {
                                                                    const currentIds = contractorForm.linkedUserIds || [];
                                                                    if (e.target.checked) {
                                                                        setContractorForm({ ...contractorForm, linkedUserIds: [...currentIds, user.id] });
                                                                    } else {
                                                                        setContractorForm({ ...contractorForm, linkedUserIds: currentIds.filter(id => id !== user.id) });
                                                                    }
                                                                }}
                                                                className="rounded border-slate-300 text-primary"
                                                            />
                                                            <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                                                                {user.name} <span className="text-[10px] text-slate-400 font-mono">({user.email})</span>
                                                            </span>
                                                        </label>
                                                    );
                                                })}
                                                {globalUsers.length === 0 && (
                                                    <p className="text-xs text-slate-400 italic p-1">No hay usuarios disponibles en el directorio global.</p>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>

                        <div className="p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 flex justify-end gap-3">
                            <button 
                                onClick={() => setIsCreateModalOpen(false)}
                                className="px-5 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={handleSaveContractor}
                                disabled={cleanRut(contractorForm.rut).length < 2}
                                className="px-6 py-2 bg-primary hover:bg-blue-600 text-white text-xs font-bold rounded-xl shadow-md transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span className="material-symbols-outlined text-[16px]">save</span>
                                {editingSubcontractorId ? 'Guardar Cambios' : 'Crear Empresa'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Subcontratos de la Empresa Seleccionada */}
            {selectedSubForContracts && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-150">
                    <div className="bg-white dark:bg-[#1a2634] rounded-3xl shadow-2xl w-full max-w-4xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[85vh] overflow-hidden">
                        <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-900/30">
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="material-symbols-outlined text-primary text-[24px]">contract</span>
                                    <h3 className="text-lg font-black text-slate-900 dark:text-white">
                                        Subcontratos: {selectedSubForContracts.razonSocial}
                                    </h3>
                                </div>
                                <p className="text-xs text-slate-500 mt-0.5 font-mono">RUT: {selectedSubForContracts.rut}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleOpenCreateSubcontract}
                                    className="bg-primary hover:bg-blue-600 text-white px-3.5 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm transition-all"
                                >
                                    <span className="material-symbols-outlined text-[16px]">add_circle</span>
                                    Nuevo Subcontrato
                                </button>
                                <button 
                                    onClick={() => setSelectedSubForContracts(null)}
                                    className="size-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-600 transition-colors"
                                >
                                    <span className="material-symbols-outlined text-[20px]">close</span>
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {/* Formulario de creación rápida de Subcontrato */}
                            {isCreateContractModalOpen && (
                                <div className="mb-6 p-5 bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900/40 rounded-2xl animate-in slide-in-from-top-2">
                                    <div className="flex items-center justify-between mb-3 pb-2 border-b border-blue-100 dark:border-blue-900/40">
                                        <h4 className="text-xs font-black text-blue-900 dark:text-blue-300 uppercase tracking-wider flex items-center gap-1.5">
                                            <span className="material-symbols-outlined text-[16px]">add</span>
                                            Crear Nuevo Subcontrato
                                        </h4>
                                        <button 
                                            onClick={() => setIsCreateContractModalOpen(false)}
                                            className="text-blue-400 hover:text-blue-600 text-xs font-bold"
                                        >
                                            Cerrar
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-600 dark:text-slate-400 uppercase mb-1">
                                                Obra / Proyecto
                                            </label>
                                            <select
                                                value={newContractForm.projectId}
                                                onChange={(e) => setNewContractForm({ ...newContractForm, projectId: e.target.value })}
                                                className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 dark:text-white outline-none"
                                            >
                                                {projects.map(p => (
                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                ))}
                                                {projects.length === 0 && <option value="">Sin obras registradas</option>}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-[10px] font-black text-slate-600 dark:text-slate-400 uppercase mb-1">
                                                Fecha Inicio
                                            </label>
                                            <input
                                                type="date"
                                                value={newContractForm.startDate}
                                                onChange={(e) => setNewContractForm({ ...newContractForm, startDate: e.target.value })}
                                                className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 dark:text-white outline-none"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-[10px] font-black text-slate-600 dark:text-slate-400 uppercase mb-1">
                                                Fecha Término
                                            </label>
                                            <input
                                                type="date"
                                                value={newContractForm.endDate}
                                                onChange={(e) => setNewContractForm({ ...newContractForm, endDate: e.target.value })}
                                                className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 dark:text-white outline-none"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-[10px] font-black text-slate-600 dark:text-slate-400 uppercase mb-1">
                                                Monto Total Contractual ($)
                                            </label>
                                            <input
                                                type="number"
                                                placeholder="Ej: 15000000"
                                                value={newContractForm.totalAmount || ''}
                                                onChange={(e) => setNewContractForm({ ...newContractForm, totalAmount: parseFloat(e.target.value) || 0 })}
                                                className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 dark:text-white outline-none"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-[10px] font-black text-slate-600 dark:text-slate-400 uppercase mb-1">
                                                % Retención Contractual
                                            </label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={newContractForm.retention}
                                                onChange={(e) => setNewContractForm({ ...newContractForm, retention: parseFloat(e.target.value) || 0 })}
                                                className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 dark:text-white outline-none"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-[10px] font-black text-slate-600 dark:text-slate-400 uppercase mb-1">
                                                Estado
                                            </label>
                                            <select
                                                value={newContractForm.status}
                                                onChange={(e) => setNewContractForm({ ...newContractForm, status: e.target.value as any })}
                                                className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 dark:text-white outline-none"
                                            >
                                                <option value="Activo">Activo</option>
                                                <option value="Borrador">Borrador</option>
                                                <option value="En Proceso">En Proceso</option>
                                            </select>
                                        </div>

                                        <div className="md:col-span-3">
                                            <label className="block text-[10px] font-black text-slate-600 dark:text-slate-400 uppercase mb-1">
                                                Observaciones / Descripción del Alcance
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Alcance del subcontrato o notas contractuales"
                                                value={newContractForm.notes}
                                                onChange={(e) => setNewContractForm({ ...newContractForm, notes: e.target.value })}
                                                className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-1.5 text-xs font-semibold text-slate-800 dark:text-white outline-none"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex justify-end gap-2 mt-4">
                                        <button
                                            onClick={() => setIsCreateContractModalOpen(false)}
                                            className="px-4 py-1.5 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-100"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={handleSaveSubcontract}
                                            className="px-5 py-1.5 bg-primary text-white rounded-lg text-xs font-bold shadow hover:bg-blue-600 transition-colors flex items-center gap-1"
                                        >
                                            <span className="material-symbols-outlined text-[16px]">check</span>
                                            Guardar Subcontrato
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Lista de subcontratos existentes de este contratista */}
                            {(() => {
                                const subContracts = contracts.filter(c => c.subcontractorId === selectedSubForContracts.id && !c.isDeleted);
                                
                                if (subContracts.length === 0) {
                                    return (
                                        <div className="p-8 text-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl text-slate-400">
                                            <span className="material-symbols-outlined text-[32px] text-slate-300 dark:text-slate-600 mb-1">description</span>
                                            <p className="font-semibold text-xs text-slate-500">No hay subcontratos registrados para esta empresa.</p>
                                            <p className="text-[11px] text-slate-400 mt-0.5">Cree un subcontrato con el botón superior "Nuevo Subcontrato".</p>
                                        </div>
                                    );
                                }

                                return (
                                    <div className="space-y-3">
                                        {subContracts.map(ctr => (
                                            <div 
                                                key={ctr.id}
                                                className="p-4 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-2xl flex flex-col md:flex-row md:items-center justify-between gap-4"
                                            >
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-mono font-bold text-xs text-slate-800 dark:text-slate-200">{ctr.id}</span>
                                                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase ${
                                                            ctr.status === 'Activo' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300' :
                                                            ctr.status === 'Borrador' ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300' :
                                                            'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                                                        }`}>
                                                            {ctr.status}
                                                        </span>
                                                    </div>
                                                    <div className="text-xs text-slate-600 dark:text-slate-400 flex flex-wrap items-center gap-x-4 gap-y-1">
                                                        <span>Vigencia: <strong className="text-slate-800 dark:text-slate-200 font-mono">{ctr.startDate}</strong> al <strong className="text-slate-800 dark:text-slate-200 font-mono">{ctr.endDate}</strong></span>
                                                        <span>Retención: <strong>{ctr.retention || 0}%</strong></span>
                                                        <span>Partidas: <strong>{ctr.items?.length || 0}</strong></span>
                                                    </div>
                                                    {ctr.notes && (
                                                        <p className="text-[11px] text-slate-500 italic mt-0.5">{ctr.notes}</p>
                                                    )}
                                                </div>

                                                <div className="flex items-center justify-between md:justify-end gap-4 border-t md:border-t-0 pt-2 md:pt-0 border-slate-200 dark:border-slate-800">
                                                    <div className="text-right">
                                                        <div className="text-[10px] text-slate-400 uppercase font-black">Monto Contractual</div>
                                                        <div className="text-sm font-black text-slate-900 dark:text-white font-mono">
                                                            ${(ctr.totalAmount || 0).toLocaleString()}
                                                        </div>
                                                    </div>

                                                    <button
                                                        onClick={() => handleDeleteSubcontract(ctr.id)}
                                                        className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded-lg transition-colors"
                                                        title="Eliminar Subcontrato"
                                                    >
                                                        <span className="material-symbols-outlined text-[18px]">delete</span>
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}
                        </div>

                        <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 flex justify-end">
                            <button
                                onClick={() => setSelectedSubForContracts(null)}
                                className="px-5 py-2 bg-slate-200 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-bold transition-colors"
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContractorManager;
